import { Flex, Spacing, Text, Textarea } from '@billy/design-system'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useComponentId, useFormContext } from '../../../../../../hooks'
import { TERMS_AND_CONDITIONS_CHARACTER_LIMIT } from '../../../../constants/termsAndConditionsCharacterLimit'
import type { InvoicingSettingsSection } from '../../../../enums/invoicingSettingsSection'
import { useTrackInvoicingSettings } from '../../../../hooks/useTrackInvoicingSettings'
import { useTrackInvoicingSettingsDebounced } from '../../../../hooks/useTrackInvoicingSettingsDebounced'
import type { DocumentTypeSettingsFormSchema } from '../../types/documentTypeSettingsFormSchema'
import { DocumentTypeSettingsCard } from '../DocumentTypeSettingsCard'
import { DocumentTypeSettingsCharacterCount } from '../DocumentTypeSettingsCharacterCount'
import { DocumentTypeSettingsSwitchRow } from '../DocumentTypeSettingsSwitchRow'

interface DocumentTypeSettingsTermsAndConditionsProps {
  trackingSection: InvoicingSettingsSection
}

export const DocumentTypeSettingsTermsAndConditions = ({
  trackingSection,
}: DocumentTypeSettingsTermsAndConditionsProps): ReactElement => {
  const { t } = useTranslation()
  const { FormItem, watch } = useFormContext<DocumentTypeSettingsFormSchema>()
  const { trackInvoicingSettings } = useTrackInvoicingSettings({ section: trackingSection })
  const headingId = useComponentId('document-type-settings-terms-and-conditions-heading')

  const termsAndConditions = watch('termsAndConditions')

  const handleDebouncedTermsAndConditionsChange = useTrackInvoicingSettingsDebounced('Terms and conditions typed', {
    section: trackingSection,
  })

  return (
    <Flex flexDirection="column" gap={Spacing.S8}>
      <Text id={headingId} weight="medium">
        {t('settings.organization.external_invoice.content.default_terms_and_conditions.label')}
      </Text>
      <DocumentTypeSettingsCard>
        <Flex flexDirection="column" gap={Spacing.S12}>
          <FormItem
            name="shouldShowTermsAndConditions"
            render={({ field: { onChange, value } }) => (
              <DocumentTypeSettingsSwitchRow
                checked={value}
                label={t('invoicing_settings.document_type_settings.show_terms_and_conditions')}
                onChange={(event) => {
                  onChange(event)
                  trackInvoicingSettings('Show terms and conditions', { checked: event.target.checked })
                }}
              />
            )}
          />
          <Flex flexDirection="column" gap={Spacing.S4}>
            <FormItem
              name="termsAndConditions"
              render={({ field: { onChange, ...rest } }) => (
                <Textarea
                  {...rest}
                  aria-labelledby={headingId}
                  maxLength={TERMS_AND_CONDITIONS_CHARACTER_LIMIT}
                  onChange={(event) => {
                    onChange(event)
                    handleDebouncedTermsAndConditionsChange()
                  }}
                  placeholder={t('invoicing_settings.document_type_settings.terms_and_conditions_placeholder')}
                  rows={2}
                />
              )}
            />
            <DocumentTypeSettingsCharacterCount
              length={termsAndConditions.length}
              limit={TERMS_AND_CONDITIONS_CHARACTER_LIMIT}
            />
          </Flex>
        </Flex>
      </DocumentTypeSettingsCard>
    </Flex>
  )
}
